import {useConfig} from 'organization/Event/Configurable'
import Config from 'organization/Event/Configurable/Config'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import React, {useState} from 'react'
import {Controller} from 'react-hook-form'
import ColorPicker from 'lib/ui/ColorPicker'
import CollapsibleSection from 'lib/ui/ConfigPanel/CollapsibleSection'
import Slider from 'lib/ui/Slider'
import StylingPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/StylingPanel'
import Select from 'lib/ui/Select'
import Option from 'lib/ui/Select/Option'
import {onUnknownChangeHandler} from 'lib/dom'
import {REMOVE} from 'Event/TemplateUpdateProvider'
import VisibilitySetting from 'organization/Marketplace/config/BlockConfig/VisibilitySettings'
import LocalizedDateTimePicker from 'lib/LocalizedDateTimePicker'
import TextField from 'lib/ui/TextField'
import {Label} from 'lib/ui/typography'
import {CountdownTimerBlock} from 'Event/Marketplace/Block/CountdownTimer'
import {useConfigurableSection} from 'organization/Marketplace/config/ConfigurableSection'
import MarginPaddingInputs from 'lib/ui/MarginPaddingInputs'
import MoveSectionButtons from 'organization/Marketplace/config/BlockConfig/MoveSectionButtons'
import {useSection} from 'Event/Marketplace/Section'
import FontSelect from 'lib/FontSelect'

interface CountdownTimerConfigProps extends CountdownTimerBlock {
  id: string
  sectionId: string
  onMoveSectionUp?: () => void
  onMoveSectionDown?: () => void
}

export default function CountdownTimerConfig(props: CountdownTimerConfigProps) {
  const {
    padding,
    alignment,
    id,
    countdownTimer,
    background,
    onMoveSectionUp,
    onMoveSectionDown,
  } = props
  const {update} = useConfigurableSection()
  const {calculateVisibility} = useSection()
  const {
    form: {control},
  } = useConfig()
  const [visibility, setVisibility] = useState(props.visibility)

  const handleRemove = () => {
    update({
      blocks: {
        [id]: REMOVE,
      },
    })
    calculateVisibility()
  }

  const save = (data: any) => {
    data.visibility = visibility
    update({
      blocks: {
        [id]: data,
      },
    })
    calculateVisibility()
  }

  return (
    <Config title="Countdown Timer Block" onSave={save} onRemove={handleRemove}>
      <SettingsPanel>
        <Controller
          name={`countdownTimer.end`}
          control={control}
          defaultValue={countdownTimer.end}
          render={({onChange, value}) => (
            <div>
              <Label>End</Label>
              <LocalizedDateTimePicker
                value={value}
                onChange={(date) => onChange(date?.toISOString())}
                fullWidth
                inputProps={{
                  'aria-label': 'end time',
                  onChange,
                }}
              />
            </div>
          )}
        />
        <Label>Description</Label>
        <Controller
          name={`countdownTimer.description`}
          control={control}
          defaultValue={countdownTimer.description}
          render={({value, onChange}) => (
            <TextField
              aria-label="description input"
              fullWidth
              onChange={onChange}
              value={value}
            />
          )}
        />
        <Label>Separator</Label>
        <Controller
          name={`countdownTimer.separator`}
          control={control}
          defaultValue={countdownTimer.separator}
          render={({value, onChange}) => (
            <TextField
              aria-label="separator input"
              fullWidth
              onChange={onChange}
              value={value}
            />
          )}
        />
        <Controller
          name={`countdownTimer.labels.days`}
          control={control}
          defaultValue={countdownTimer.labels.days}
          render={({value, onChange}) => (
            <TextField
              aria-label="days label input"
              fullWidth
              onChange={onChange}
              value={value}
              label="Days Label"
            />
          )}
        />
        <Controller
          name={`countdownTimer.labels.hours`}
          control={control}
          defaultValue={countdownTimer.labels.hours}
          render={({value, onChange}) => (
            <TextField
              aria-label="hours label input"
              fullWidth
              onChange={onChange}
              value={value}
              label="Hours Label"
            />
          )}
        />
        <Controller
          name={`countdownTimer.labels.minutes`}
          control={control}
          defaultValue={countdownTimer.labels.minutes}
          render={({value, onChange}) => (
            <TextField
              aria-label="minutes label input"
              fullWidth
              onChange={onChange}
              value={value}
              label="Minutes Label"
            />
          )}
        />
        <Controller
          name={`countdownTimer.labels.seconds`}
          control={control}
          defaultValue={countdownTimer.labels.seconds}
          render={({value, onChange}) => (
            <TextField
              aria-label="seconds label input"
              fullWidth
              onChange={onChange}
              value={value}
              label="Seconds Label"
            />
          )}
        />
        <VisibilitySetting
          visibility={visibility}
          setVisibility={setVisibility}
        />
        <MoveSectionButtons
          onMoveUp={onMoveSectionUp}
          onMoveDown={onMoveSectionDown}
        />
      </SettingsPanel>
      <StylingPanel>
        <CollapsibleSection
          label="Typography"
          storageKey={`blocks-${id}-typography`}
        >
          <Label>Font</Label>
          <Controller
            control={control}
            name="countdownTimer.font"
            defaultValue={countdownTimer.font}
            render={({value, onChange}) => (
              <FontSelect value={value} onChange={onChange} />
            )}
          />
          <Controller
            name="countdownTimer.fontSize"
            defaultValue={countdownTimer.fontSize}
            control={control}
            render={({value, onChange}) => (
              <Slider
                min={1}
                max={60}
                onChange={onChange}
                valueLabelDisplay="off"
                value={value}
                label="Font Size"
              />
            )}
          />
        </CollapsibleSection>

        <CollapsibleSection
          label="Number Block"
          storageKey={`blocks-${id}-number-block`}
        >
          <Controller
            name={`countdownTimer.numberColor`}
            control={control}
            defaultValue={countdownTimer.numberColor}
            render={({value, onChange}) => (
              <ColorPicker label="Text Color" color={value} onPick={onChange} />
            )}
          />
          <Controller
            name={`countdownTimer.numberBackgroundColor`}
            control={control}
            defaultValue={countdownTimer.numberBackgroundColor}
            render={({value, onChange}) => (
              <ColorPicker
                label="Background Color"
                color={value}
                onPick={onChange}
              />
            )}
          />
          <Controller
            name={`countdownTimer.numberBackgroundOpacity`}
            defaultValue={countdownTimer.numberBackgroundOpacity}
            control={control}
            render={({value, onChange}) => (
              <Slider
                label="Background Opacity"
                unit="%"
                valueLabelDisplay="auto"
                aria-label="number background opacity"
                value={value}
                onChange={onChange}
                step={1}
                min={0}
                max={100}
              />
            )}
          />
          <Controller
            name={`countdownTimer.numberBackgroundRadius`}
            defaultValue={countdownTimer.numberBackgroundRadius}
            control={control}
            render={({value, onChange}) => (
              <Slider
                min={0}
                max={100}
                step={1}
                onChange={onChange}
                value={value}
                label="Background Radius"
                valueLabelDisplay="auto"
                unit="%"
              />
            )}
          />
          <Controller
            name={`countdownTimer.numberBackgroundWidth`}
            control={control}
            defaultValue={countdownTimer.numberBackgroundWidth}
            render={({value, onChange}) => (
              <Slider
                label="Min Width"
                unit="px"
                valueLabelDisplay="auto"
                aria-label="number block width"
                value={value}
                onChange={onChange}
                step={1}
                min={0}
                max={300}
              />
            )}
          />
          <Controller
            name={`countdownTimer.numberBackgroundHeight`}
            control={control}
            defaultValue={countdownTimer.numberBackgroundHeight}
            render={({value, onChange}) => (
              <Slider
                label="Min Height"
                unit="px"
                valueLabelDisplay="auto"
                aria-label="number block height"
                value={value}
                onChange={onChange}
                step={1}
                min={0}
                max={500}
              />
            )}
          />
        </CollapsibleSection>

        <CollapsibleSection
          label="Block Background"
          storageKey={`blocks-${id}-background`}
        >
          <Controller
            name={`background.color`}
            control={control}
            defaultValue={background.color}
            render={({value, onChange}) => (
              <ColorPicker label="Color" color={value} onPick={onChange} />
            )}
          />
          <Controller
            name={`background.opacity`}
            defaultValue={background.opacity}
            control={control}
            render={({value, onChange}) => (
              <Slider
                label="Opacity"
                unit="%"
                valueLabelDisplay="auto"
                aria-label="block background opacity"
                value={value}
                onChange={onChange}
                step={1}
                min={0}
                max={100}
              />
            )}
          />
        </CollapsibleSection>

        <CollapsibleSection
          label="Timer Background"
          storageKey={`blocks-${id}-background`}
        >
          <Controller
            name={`countdownTimer.backgroundColor`}
            control={control}
            defaultValue={countdownTimer.backgroundColor}
            render={({value, onChange}) => (
              <ColorPicker label="Color" color={value} onPick={onChange} />
            )}
          />
          <Controller
            name={`countdownTimer.backgroundOpacity`}
            defaultValue={countdownTimer.backgroundOpacity}
            control={control}
            render={({value, onChange}) => (
              <Slider
                label="Opacity"
                unit="%"
                valueLabelDisplay="auto"
                aria-label="block background opacity"
                value={value}
                onChange={onChange}
                step={1}
                min={0}
                max={100}
              />
            )}
          />
        </CollapsibleSection>
        <CollapsibleSection
          label="Description & Separator Text"
          storageKey={`blocks-${id}-description`}
        >
          <Controller
            name={`countdownTimer.textColor`}
            control={control}
            defaultValue={countdownTimer.textColor}
            render={({value, onChange}) => (
              <ColorPicker label="Color" color={value} onPick={onChange} />
            )}
          />
        </CollapsibleSection>
        <CollapsibleSection
          label="Alignment"
          storageKey={`blocks-${id}-alignment`}
        >
          <Controller
            name={`alignment.vertical`}
            defaultValue={alignment.vertical}
            control={control}
            render={({value, onChange}) => (
              <Select
                fullWidth
                label="Vertical"
                value={value}
                onChange={onUnknownChangeHandler(onChange)}
              >
                <Option value="start">Top</Option>
                <Option value="center">Center</Option>
                <Option value="end">Bottom</Option>
              </Select>
            )}
          />
          <Controller
            name={`alignment.horizontal`}
            defaultValue={alignment.horizontal}
            control={control}
            render={({value, onChange}) => (
              <Select
                fullWidth
                label="Horizontal"
                value={value}
                onChange={onUnknownChangeHandler(onChange)}
              >
                <Option value="left">Left</Option>
                <Option value="center">Center</Option>
                <Option value="right">Right</Option>
              </Select>
            )}
          />
        </CollapsibleSection>
        <CollapsibleSection
          label="Advanced Spacing"
          storageKey={`blocks-${id}-spacing`}
          open={false}
        >
          <Label>Padding</Label>
          <MarginPaddingInputs
            control={control}
            namePrefix={`padding`}
            values={padding}
          />
        </CollapsibleSection>
      </StylingPanel>
    </Config>
  )
}
